import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Upload, Sun, Crosshair, CheckCircle,
  AlertCircle, ArrowRight, ArrowLeft, Activity, Image, Info
} from 'lucide-react';
import { AppShell } from '../components/Layout';
import { usePatient } from '../contexts/PatientContext';
import { API_URL, API_KEY } from '../config';

const STEPS = [
  { id: 1, label: 'Patient Info',   shortLabel: 'Patient'  },
  { id: 2, label: 'Prepare Area',   shortLabel: 'Prepare'  },
  { id: 3, label: 'Apply Markers',  shortLabel: 'Markers'  },
  { id: 4, label: 'Lighting',       shortLabel: 'Lighting' },
  { id: 5, label: 'Capture & Send', shortLabel: 'Capture'  },
];

function StepsBar({ current }) {
  return (
    <div
      className="steps-bar"
      style={{
        marginBottom: 36,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '0 4px',
        gap: 0,
      }}
    >
      {STEPS.map((s, idx) => (
        <React.Fragment key={s.id}>
          <div
            className={`step-item ${current > s.id ? 'completed' : ''} ${current === s.id ? 'active' : ''}`}
            style={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 0,
              width: 44,
            }}
          >
            <div className="step-circle">
              {current > s.id ? <CheckCircle size={14} /> : s.id}
            </div>
            <div
              className="step-label"
              style={{
                fontSize: '0.65rem',
                marginTop: 5,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                letterSpacing: 0,
              }}
            >
              {s.shortLabel}
            </div>
          </div>

          {idx < STEPS.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                minWidth: 10,
                background: current > s.id
                  ? 'var(--blue-primary, #1B6CA8)'
                  : 'var(--border-light, #e2e8f0)',
                alignSelf: 'flex-start',
                marginTop: 14,
                transition: 'background 0.3s',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function normaliseApiResult(raw) {
  const data = raw?.Result ?? raw;

  let imgUrl = data?.Img_URL;
  if (Array.isArray(imgUrl)) imgUrl = imgUrl[0] ?? 'N/A';
  imgUrl = String(imgUrl ?? 'N/A');

  const report = Array.isArray(data?.Report) ? data.Report : [];

  const tissue = Array.isArray(data?.['Wound Tissue (%)'])
    ? data['Wound Tissue (%)']
    : data?.['Wound Tissue (%)']
    ? [data['Wound Tissue (%)']]
    : [];

  let stageRaw = data?.Stage;
  if (Array.isArray(stageRaw)) stageRaw = stageRaw[0] ?? stageRaw;
  let stage = [];
  if (stageRaw && typeof stageRaw === 'object' && !Array.isArray(stageRaw)) {
    stage = Object.entries(stageRaw).map(([name, conf]) => ({
      Stage: name,
      Confidence: typeof conf === 'number' ? conf : parseFloat(conf) || 0,
    }));
  } else if (Array.isArray(stageRaw) && stageRaw.length && stageRaw[0]?.Stage) {
    stage = stageRaw;
  }

  let adviceRaw = data?.Advice;
  let advice = [];
  if (Array.isArray(adviceRaw)) {
    const inner = adviceRaw[0];
    if (Array.isArray(inner)) advice = inner;
    else advice = adviceRaw.filter(v => typeof v === 'string');
  } else if (typeof adviceRaw === 'string') {
    advice = [adviceRaw];
  }

  return {
    AnalysisID:        data?.AnalysisID  ?? 'N/A',
    full_name:         data?.full_name   ?? '',
    patient_id:        data?.patient_id  ?? '',
    Img_URL:           imgUrl,
    Report:            report,
    'Wound Tissue (%)': tissue,
    Stage:             stage,
    Advice:            advice,
  };
}

export default function NewAnalysisScreen() {
  const navigate  = useNavigate();
  const { patient } = usePatient();
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [preview, setPreview] = useState(null);
  const [base64, setBase64]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const next = () => {
    if (step === 1 && (!patient?.patient_id || !patient?.full_name)) {
      setError('Please register a patient first before proceeding.');
      return;
    }
    setError('');
    setStep(s => Math.min(s + 1, STEPS.length));
  };

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, HEIC).');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      setBase64(e.target.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleAnalyze = async () => {
    if (!base64) { setError('Please select or capture a wound image first.'); return; }
    if (!patient?.patient_id || !patient?.full_name) {
      setError('Patient information is missing. Please register a patient first.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const payload = {
        AnalysisID:  `analysis-${Date.now()}`,
        ExpertName:  'WebUser',
        Images: [{ ImageID: `image-${Date.now()}`, ImageBase64: base64 }],
        patient_id:  patient.patient_id,
        full_name:   patient.full_name,
        age:         patient.age,
        phone:       patient.phone,
      };

      const res = await fetch(`${API_URL}/analyze`, {
        method:  'POST',
        headers: {
          'Content-Type':       'application/json',
          'Cytometrix-API-KEY': API_KEY,
          'X-API-HOST':         'web-app',
        },
        body: JSON.stringify(payload),
      });

      let raw;
      try { raw = await res.json(); } catch { raw = null; }

      if (!res.ok) {
        const errText =
          raw?.Result?.Error?.ErrorText ||
          raw?.Error?.ErrorText ||
          raw?.message ||
          'Analysis failed. Please ensure the image is clear and includes calibration markers.';
        setError(errText);
        return;
      }

      if (!raw) {
        setError('Empty response from server. Please try again.');
        return;
      }

      const normalisedResult = normaliseApiResult(raw);
      navigate('/result', { state: { result: normalisedResult } });

    } catch (err) {
      console.error('Analyze error:', err);
      setError('Cannot connect to server. Please check your network connection.');
    } finally {
      setLoading(false);
    }
  };

  const stepContent = {
    1: (
      <div>
        {patient?.patient_id ? (
          <div style={{ padding: 16, background: 'var(--success-bg)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(5,150,105,0.2)', display: 'flex', gap: 12, marginBottom: 20 }}>
            <CheckCircle size={20} color="var(--success)" style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, color: 'var(--success)', marginBottom: 2 }}>Patient Registered</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <strong>{patient.full_name}</strong>&nbsp;·&nbsp;
                ID: <code style={{ fontFamily: 'var(--font-mono)', background: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: 4 }}>{patient.patient_id}</code>
                {patient.age && <>&nbsp;·&nbsp;Age: {patient.age}</>}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: 16, background: 'var(--warning-bg)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(217,119,6,0.2)', display: 'flex', gap: 12, marginBottom: 20 }}>
            <AlertCircle size={20} color="var(--warning)" style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>No Patient Selected</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
                You must register or select a patient before running an analysis.
              </div>
              <button className="btn btn-sm btn-primary" onClick={() => navigate('/patient')}>
                Register Patient
              </button>
            </div>
          </div>
        )}
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          Ensure all patient information is correctly entered. Clean the wound and surrounding skin gently, and ensure the area is dry before capturing an image.
        </p>
      </div>
    ),

    2: (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[
          { label: 'Clean the wound',    desc: 'Gently cleanse and pat dry the wound and surrounding skin.' },
          { label: 'Remove dressings',   desc: 'Remove all dressings and bandages before imaging.' },
          { label: 'Good lighting',      desc: 'Natural daylight or uniform overhead lighting is ideal.' },
          { label: 'Stable positioning', desc: 'Ensure the patient is comfortable and stable for the photo.' },
        ].map(({ label, desc }) => (
          <div key={label} style={{ padding: 14, background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
            <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--navy)', marginBottom: 4 }}>✓ {label}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{desc}</div>
          </div>
        ))}
      </div>
    ),

    3: (
      <div>
        <div className="alert alert-info" style={{ marginBottom: 20 }}>
          <Info size={16} style={{ flexShrink: 0 }} />
          <span>
            Calibration markers allow the AI to calculate real-world wound dimensions.
            If no markers are present, measurements will use an estimated scale factor.
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { color: '#2563EB', name: 'Blue',     desc: 'Place on one side of the wound' },
            { color: '#16A34A', name: 'Green',    desc: 'Place on the opposite side' },
            { color: '#CA8A04', name: 'Yellow ×2', desc: 'Alternative: two yellow markers' },
          ].map(({ color, name, desc }) => (
            <div key={name} style={{ padding: 16, background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: `2px solid ${color}20`, textAlign: 'center' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: color, margin: '0 auto 10px' }} />
              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--navy)' }}>{name}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>{desc}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 14, lineHeight: 1.6 }}>
          Markers should be <strong>circular</strong> and approximately <strong>0.75 cm</strong> in diameter.
          Place them flat against the skin on opposite sides of the wound.
        </p>
      </div>
    ),

    4: (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[
          { icon: Sun,      color: '#D97706', bg: 'rgba(217,119,6,0.08)',    title: 'Use Natural Light',         desc: 'Natural daylight or uniform overhead lighting. Avoid harsh shadows across the wound.' },
          { icon: Camera,   color: '#DC2626', bg: 'rgba(220,38,38,0.08)',    title: 'No Flash',                  desc: 'Do not use camera flash — it creates glare and distorts colour, depth, and tissue classification.' },
          { icon: Crosshair,color: '#1B6CA8', bg: 'rgba(27,108,168,0.08)',   title: 'Camera Directly Above',     desc: 'Position camera perpendicular to the wound surface. Avoid angled shots.' },
          { icon: Image,    color: '#059669', bg: 'rgba(5,150,105,0.08)',    title: 'Full Wound in Frame',        desc: 'Ensure the complete wound boundary and both calibration markers are clearly visible.' },
        ].map(({ icon: Icon, color, bg, title, desc }) => (
          <div key={title} style={{ padding: 16, borderRadius: 'var(--radius-md)', background: bg, border: `1px solid ${color}20`, display: 'flex', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={18} color={color} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--navy)', marginBottom: 3 }}>{title}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>
    ),

    5: (
      <div>
        {!preview ? (
          <div
            className={`upload-zone ${dragging ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
            <div className="upload-zone-icon"><Upload size={24} /></div>
            <div className="upload-zone-text">Drop wound image here or click to browse</div>
            <div className="upload-zone-sub">JPG, PNG, HEIC — up to 20 MB</div>
          </div>
        ) : (
          <div>
            <div className="image-preview-wrap" style={{ marginBottom: 16 }}>
              <img src={preview} alt="Wound preview" />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setPreview(null); setBase64(null); setError(''); }}
              >
                Replace Image
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={handleAnalyze}
                disabled={loading}
              >
                {loading
                  ? <><span className="spinner" /> Analysing wound…</>
                  : <><Activity size={16} /> Run AI Analysis</>
                }
              </button>
            </div>
            {loading && (
              <div style={{ marginTop: 16, padding: 14, background: 'rgba(27,108,168,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(27,108,168,0.15)', fontSize: '0.875rem', color: 'var(--blue-primary)' }}>
                ⏳ Processing image — this may take 15–60 seconds depending on server load.
              </div>
            )}
          </div>
        )}
      </div>
    ),
  };

  return (
    <AppShell title="New Wound Analysis">
      {/* ── Centered page wrapper ── */}
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px 60px',
        }}
      >
        {/* ── Page header — centred ── */}
        <div
          className="page-header"
          style={{ textAlign: 'center', marginBottom: 36, width: '100%', maxWidth: 680 }}
        >
          <h1 className="page-title" style={{ marginBottom: 6 }}>Wound Analysis</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>
            Follow each step carefully for accurate AI-powered results
          </p>
        </div>

        {/* ── Content column — centred, constrained width ── */}
        <div style={{ width: '100%', maxWidth: 680 }}>
          <StepsBar current={step} />

          {error && (
            <div className="alert alert-error" style={{ marginBottom: 24 }}>
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="card-title">
                Step {step} of {STEPS.length}: {STEPS[step - 1].label}
              </div>
            </div>
            {stepContent[step]}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button
              className="btn btn-secondary"
              onClick={() => step > 1 ? setStep(s => s - 1) : navigate('/dashboard')}
            >
              <ArrowLeft size={16} /> {step > 1 ? 'Previous Step' : 'Dashboard'}
            </button>
            {step < STEPS.length && (
              <button className="btn btn-primary" onClick={next}>
                Next Step <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
